import {
  Banner, DataTable, InlineGrid, Layout, LegacyCard,
  Page, Spinner, Text,
} from "@shopify/polaris";
import { useEffect, useMemo, useState } from "react";
import { admin_store_performance } from "../api";

const presets = [
  { id: "today", label: "Today" }, { id: "week", label: "Last 7 days" },
  { id: "month30", label: "Last 30 days" }, { id: "month", label: "This month" },
];
function dateRange(preset) {
  const end = new Date();
  const start = new Date(end);
  if (preset === "month") start.setUTCDate(1);
  else start.setUTCDate(start.getUTCDate() - (preset === "week" ? 6 : preset === "month30" ? 29 : 0));
  return { start_date: start.toISOString().slice(0, 10), end_date: end.toISOString().slice(0, 10) };
}
const initialFilters = () => ({ ...dateRange("month30"), order_type: "all" });
const displayDate = (date) => new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(date + "T00:00:00Z"));
const orderTypes = [
  { label: "All orders", value: "all" },
  { label: "Online orders", value: "online" },
  { label: "COD orders", value: "cod" },
];

function money(amounts = {}) {
  const entries = Object.entries(amounts);
  if (!entries.length) return "—";
  return entries.map(([currency, amount]) => {
    const value = Number(amount);
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 4 }).format(value);
    } catch {
      return `${amount} ${currency}`;
    }
  }).join(" · ");
}

export default function StorePerformance() {
  const [applied, setApplied] = useState(initialFilters);
  const [draft, setDraft] = useState(() => ({ ...applied }));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterError, setFilterError] = useState("");
  const activePreset = presets.find(({ id }) => {
    const range = dateRange(id);
    return range.start_date === draft.start_date && range.end_date === draft.end_date;
  })?.id;
  const changed = Object.keys(applied).some((key) => draft[key] !== applied[key]);
  const updateDraft = (next) => { setDraft((current) => ({ ...current, ...next })); setFilterError(""); };
  const apply = (event) => {
    event.preventDefault();
    if (!draft.start_date || !draft.end_date) return setFilterError("Choose both a start date and an end date.");
    if (draft.start_date > draft.end_date) return setFilterError("Start date must be on or before end date.");
    if ((Date.parse(draft.end_date) - Date.parse(draft.start_date)) / 86400000 > 366) return setFilterError("Choose a date range of 367 days or less.");
    setFilterError("");
    setApplied({ ...draft });
  };

  useEffect(() => {
    let active = true;
    setLoading(true); setError("");
    admin_store_performance(applied).then((response) => {
      if (!active) return;
      if (response.status === 200) setReport(response.data);
      else setError(response.msg || "Unable to load store performance.");
    }).catch((requestError) => {
      if (active) setError(requestError?.response?.data?.msg || "Unable to load store performance.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [applied]);

  const rows = useMemo(() => (report?.stores || []).map((store) => [
    <div><Text as="span" variant="bodyMd" fontWeight="semibold">{store.shop_name || `Shop ${store.shop_id}`}</Text>{store.app_name && <Text as="p" tone="subdued">{store.app_name}</Text>}</div>,
    store.is_active ? "Active" : "Inactive",
    store.order_count,
    store.confirmed_order_count,
    money(store.confirmed_amounts),
  ]), [report]);

  return (
    <Page title="Store performance" subtitle="Order performance by source Shopify store">
      <Layout>
        {error && <Layout.Section><Banner tone="critical" title="Unable to load report">{error}</Banner></Layout.Section>}
        <Layout.Section>
          <form className="sa-performance-filters" onSubmit={apply} noValidate aria-label="Performance filters">
            <div className="sa-performance-filter-heading">
              <div className="sa-performance-range-heading"><h2>Date range</h2><span className="sa-timezone-badge">UTC</span></div>
              <div className="sa-performance-presets" role="group" aria-label="Quick date ranges">
                {presets.map(({ id, label }) => <button key={id} type="button" aria-pressed={activePreset === id} onClick={() => updateDraft(dateRange(id))}>{label}</button>)}
              </div>
            </div>
            <div className="sa-performance-filter-fields">
              <div className="sa-performance-date-range">
                <label htmlFor="performance-start">Start date<input id="performance-start" type="date" value={draft.start_date} onChange={(event) => updateDraft({ start_date: event.target.value })} aria-describedby={filterError ? "performance-date-error" : undefined} aria-invalid={Boolean(filterError)} /></label>
                <span className="sa-performance-date-separator" aria-hidden="true">to</span>
                <label htmlFor="performance-end">End date<input id="performance-end" type="date" value={draft.end_date} onChange={(event) => updateDraft({ end_date: event.target.value })} aria-describedby={filterError ? "performance-date-error" : undefined} aria-invalid={Boolean(filterError)} /></label>
              </div>
              <label htmlFor="performance-type">Order type<select id="performance-type" value={draft.order_type} onChange={(event) => updateDraft({ order_type: event.target.value })}>{orderTypes.map(({ label, value }) => <option key={value} value={value}>{label}</option>)}</select></label>
              <button className="sa-button sa-button--primary sa-performance-apply" type="submit" disabled={loading}>{loading ? "Loading..." : "Apply"}</button>
            </div>
            {filterError && <p className="sa-performance-filter-error" id="performance-date-error" role="alert">{filterError}</p>}
            <div className="sa-performance-filter-status" role="status">
              <span>{report?.filters ? "Showing " + displayDate(report.filters.start_date) + " - " + displayDate(report.filters.end_date) + " (UTC) / " + (orderTypes.find(({ value }) => value === report.filters.order_type)?.label || "All orders") : "Select a date range to view your report."}</span>
              <span>{changed ? "Changes not applied" : loading ? "Updating report..." : "Dates include the full day"}</span>
            </div>
          </form>
        </Layout.Section>
        <Layout.Section>
          <Banner>
            Counts include orders created in the selected UTC dates. Confirmed online orders have status 1; confirmed COD orders have status 3 (accepted). Amounts are grouped by currency and represent order value, not signed delivery or repayment.
          </Banner>
        </Layout.Section>
        <Layout.Section>
          {loading && !report ? <Spinner accessibilityLabel="Loading store performance" /> : <InlineGrid columns={{ xs: 1, sm: 3 }} gap="400">
            <LegacyCard title="Orders" sectioned><Text as="p" variant="headingLg">{report?.totals?.order_count ?? 0}</Text></LegacyCard>
            <LegacyCard title="Confirmed orders" sectioned><Text as="p" variant="headingLg">{report?.totals?.confirmed_order_count ?? 0}</Text></LegacyCard>
            <LegacyCard title="Confirmed order value" sectioned><Text as="p" variant="headingMd">{money(report?.totals?.confirmed_amounts)}</Text></LegacyCard>
          </InlineGrid>}
        </Layout.Section>
        <Layout.Section>
          <LegacyCard title="Performance by store">
            <DataTable
              columnContentTypes={["text", "text", "numeric", "numeric", "text"]}
              headings={["Shop", "Status", "Orders", "Confirmed", "Confirmed order value"]}
              rows={rows}
              loading={loading}
              emptyState={<Text as="p" tone="subdued">No stores or orders match this period.</Text>}
            />
          </LegacyCard>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
